import React, { useState } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { COLORES_ETAPA } from '../../data/flujos';
import type { ColorEtapa, EtapaFlujo } from '../../types';
const claseInput = 'h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-2xs font-semibold uppercase tracking-wide text-gris-500';
export function EditorEtapa({
  etapa,
  orden,
  total,
  tiposDocumento,
  onGuardar,
  onCancelar
}: {
  etapa: EtapaFlujo;
  orden: number;
  total: number;
  tiposDocumento: string[];
  onGuardar: (etapa: EtapaFlujo, nuevoOrden: number) => void;
  onCancelar: () => void;
}) {
  const [borrador, setBorrador] = useState<EtapaFlujo>(etapa);
  const [posicion, setPosicion] = useState(orden);
  const [error, setError] = useState('');
  const cambiar = <C extends keyof EtapaFlujo,>(campo: C, valor: EtapaFlujo[C]) => setBorrador(prev => ({
    ...prev,
    [campo]: valor
  }));
  const alternarDocumento = (tipo: string) => setBorrador(prev => ({
    ...prev,
    documentosEsperados: prev.documentosEsperados.includes(tipo) ? prev.documentosEsperados.filter(t => t !== tipo) : [...prev.documentosEsperados, tipo]
  }));
  const guardar = () => {
    if (!borrador.nombre.trim()) {
      setError('El nombre de la etapa es obligatorio.');
      return;
    }
    onGuardar({
      ...borrador,
      nombre: borrador.nombre.trim(),
      etiqueta: borrador.etiqueta.trim(),
      diasSugeridos: borrador.tienePlazo ? Math.max(1, Number(borrador.diasSugeridos) || 1) : 0
    }, posicion);
  };
  return <div className="rounded-lg border border-marino-300 bg-white p-4 shadow-panel" data-id="element-667" data-mp="1avlrg0bew091">
      <div className="grid gap-4 md:grid-cols-3" data-id="element-668" data-mp="1fqhm788191pu">
        <label className="md:col-span-2" data-id="element-669" data-mp="3viymrvqpl3mk">
          <span className={claseEtiqueta} data-id="element-670" data-mp="3vi5egqc3q62b">Nombre de la etapa *</span>
          <input autoFocus className={`mt-1.5 ${claseInput}`} value={borrador.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Ej. Audiencia inicial" data-id="element-671" data-mp="4e8d5gcpv5ov" />
        </label>
        <label data-id="element-672" data-mp="1xa16ugj0jxrf">
          <span className={claseEtiqueta} data-id="element-673" data-mp="1x9hoiypgb7cv">Orden</span>
          <select className={`mt-1.5 ${claseInput}`} value={posicion} onChange={e => setPosicion(Number(e.target.value))} data-id="element-674" data-mp="1x86on9z4mjr1">
            {Array.from({
            length: total
          }).map((_, i) => <option key={i} value={i} data-id="element-675" data-mp="1jai1fvhl70it" data-mpi={i}>
                {i + 1} de {total}
              </option>)}
          </select>
        </label>

        <label className="md:col-span-2" data-id="element-676" data-mp="1eboy2vw0kpwd">
          <span className={claseEtiqueta} data-id="element-677" data-mp="19c29xrendp8s">Descripción corta</span>
          <input className={`mt-1.5 ${claseInput}`} value={borrador.descripcion} onChange={e => cambiar('descripcion', e.target.value)} placeholder="Qué ocurre procesalmente en esta etapa" data-id="element-678" data-mp="19dwuh4m0bfcc" />
        </label>
        <label data-id="element-679" data-mp="3bae10w5yo1q7">
          <span className={claseEtiqueta} data-id="element-680" data-mp="3b8unveflq6sn">Etiqueta</span>
          <input className={`mt-1.5 ${claseInput}`} value={borrador.etiqueta} onChange={e => cambiar('etiqueta', e.target.value)} placeholder="Ej. Audiencia" data-id="element-681" data-mp="3g8fnzs6icl6y" />
        </label>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-682" data-mp="3pxz6uxu80f1t">
        <fieldset className="rounded border border-gris-200 px-3 py-3" data-id="element-683" data-mp="2sb088hjywx1a">
          <legend className={claseEtiqueta} data-id="element-684" data-mp="2sd4idh8gmsa0">Plazo asociado</legend>
          <label className="flex items-center gap-2 text-sm text-gris-700" data-id="element-685" data-mp="2xad39pkztaey">
            <input type="checkbox" checked={borrador.tienePlazo} onChange={e => cambiar('tienePlazo', e.target.checked)} className="h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-686" data-mp="328o9zrfzr4sy" />
            Esta etapa tiene plazo
          </label>
          {borrador.tienePlazo && <label className="mt-3 block" data-id="element-687" data-mp="mjwgu0ziav6i">
              <span className={claseEtiqueta} data-id="element-688" data-mp="k2ln0m0xnfap">Días sugeridos</span>
              <input type="number" min={1} max={365} className={`tabular mt-1.5 ${claseInput}`} value={borrador.diasSugeridos} onChange={e => cambiar('diasSugeridos', Number(e.target.value))} data-id="element-689" data-mp="k4y99bdq4btk" />
              <span className="mt-1 block text-2xs text-gris-400" data-id="element-690" data-mp="131zix9i8303u">
                Se usan para proponer la fecha de vencimiento al avanzar de etapa.
              </span>
            </label>}
        </fieldset>

        <fieldset className="rounded border border-gris-200 px-3 py-3" data-id="element-691" data-mp="1csbysof3e53n">
          <legend className={claseEtiqueta} data-id="element-692" data-mp="1ctn6v0fklz0k">Hito y color</legend>
          <label className="flex items-center gap-2 text-sm text-gris-700" data-id="element-693" data-mp="17t94nz0nhjag">
            <input type="checkbox" checked={borrador.esHito} onChange={e => cambiar('esHito', e.target.checked)} className="h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-694" data-mp="1mr1kyte0ubpp" />
            Marcar como hito (genera alerta automática)
          </label>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-695" data-mp="25dqum3ocadxc">
            {COLORES_ETAPA.map(c => <button key={c.clave} type="button" onClick={() => cambiar('color', c.clave as ColorEtapa)} aria-label={c.nombre} aria-pressed={borrador.color === c.clave} className={`flex h-8 items-center gap-2 rounded border px-2.5 text-xs transition-colors duration-150 ${borrador.color === c.clave ? 'border-marino-700 bg-marino-50 font-semibold text-marino-800' : 'border-gris-200 text-gris-600 hover:border-gris-300'}`} data-id="element-696" data-mp="1rdpx8y7a9omm">
                <span className={`h-3 w-3 rounded-sm ${c.muestra}`} aria-hidden="true" data-id="element-697" data-mp="yqlw54bb9ug4" />
                {c.nombre}
              </button>)}
          </div>
        </fieldset>
      </div>

      <fieldset className="mt-4" data-id="element-698" data-mp="klmo2fs1159l">
        <legend className={claseEtiqueta} data-id="element-699" data-mp="ftlgv89pu4qf">Documentos esperados en esta etapa</legend>
        <p className="mt-1 text-2xs text-gris-400" data-id="element-700" data-mp="frqtlnaz20j6">
          Opciones tomadas del catálogo de documentos vigente para esta materia.
        </p>
        <div className="mt-2 flex flex-wrap gap-2" data-id="element-701" data-mp="5w71zd3fxo08">
          {[...tiposDocumento, ...borrador.documentosEsperados.filter(d => !tiposDocumento.includes(d))].map(t => {
          const activo = borrador.documentosEsperados.includes(t);
          return <button key={t} type="button" onClick={() => alternarDocumento(t)} aria-pressed={activo} className={`h-8 rounded-full border px-3 text-xs transition-colors duration-150 ${activo ? 'border-marino-800 bg-marino-800 font-medium text-white' : 'border-gris-200 text-gris-600 hover:border-gris-300'}`} data-id="element-702" data-mp="tjynn7avfde6">
                {t}
              </button>;
        })}
        </div>
      </fieldset>

      {error && <p role="alert" className="mt-3 text-xs font-medium text-peligro-600" data-id="element-703" data-mp="1yr891ry4bson">
          {error}
        </p>}

      <div className="mt-4 flex justify-end gap-2" data-id="element-704" data-mp="23kkkf0dse2zg">
        <button type="button" onClick={onCancelar} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-705" data-mp="28icm2m48h3gu">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-706" data-mp="2hgob4jpgbt76" />
          Cancelar
        </button>
        <button type="button" onClick={guardar} className="inline-flex h-9 items-center gap-1.5 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-707" data-mp="2mh2jk2uyt55q">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-708" data-mp="2u0uz6fjre3sz" />
          Guardar etapa
        </button>
      </div>
    </div>;
}